import { useState } from 'react';

import { adminApi } from '../adminApi';

// Per-row decommission / restore / delete controls for the admin Boats list
// (ADR 0165). Same arm → fire shape as ManualReviewActions: every destructive
// verb renders as `.admin-button-danger`, and clicking it swaps the row's
// controls for a confirm that names the consequence in full before anything
// happens.
//
// Restore is the one non-destructive verb here, so it is not gated.
//
// Decommission and Delete are NOT the same kind of dangerous, and the copy has
// to make that obvious — decommission is reversible from this very screen,
// delete is not reversible at all. Delete is deliberately available in any
// state (an operator asked to erase a boat should not have to decommission it
// first); this two-step confirm is the only guard.

export type BoatAction = 'decommission' | 'restore' | 'delete';

interface BoatAdminActionsProps {
  boatId: string;
  boatName: string;
  decommissioned: boolean;
  /** Called after a successful action so the list can reload. */
  onDone: (action: BoatAction) => void;
}

const CONFIRM_COPY: Record<'decommission' | 'delete', string> = {
  decommission:
    'Hide this boat from its owners? They lose access to it and stop getting its alarm ' +
    'notifications. All data is retained and you can restore it here.',
  delete:
    'Permanently delete this boat and ALL of its data — telemetry, positions, alarms, and ' +
    'history. This cannot be undone.',
};

export function BoatAdminActions({
  boatId,
  boatName,
  decommissioned,
  onDone,
}: BoatAdminActionsProps): React.ReactElement {
  const [arming, setArming] = useState<'decommission' | 'delete' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Arm / disarm, always clearing a stale error from a prior failed attempt so
  // the operator never sees a leftover message beside a fresh confirm.
  function setArmed(verb: 'decommission' | 'delete' | null): void {
    setError(null);
    setArming(verb);
  }

  async function run(action: BoatAction): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      if (action === 'delete') {
        await adminApi(`/v1/admin/boats/${boatId}`, { method: 'DELETE' });
      } else {
        await adminApi(`/v1/admin/boats/${boatId}/${action}`, { method: 'POST' });
      }
      setArming(null);
      onDone(action);
    } catch (err) {
      const apiMessage = (err as { body?: { message?: string } }).body?.message;
      setError(apiMessage ?? (err instanceof Error ? err.message : `Failed to ${action} boat.`));
    } finally {
      setBusy(false);
    }
  }

  // While a destructive verb is armed, show only its confirm so the armed
  // action and the other verbs can't interleave.
  if (arming !== null) {
    return (
      <span className="admin-confirm">
        <span className="admin-confirm-prompt">
          {boatName}: {CONFIRM_COPY[arming]}
        </span>
        <button
          type="button"
          className="admin-button-danger"
          data-testid={`admin-boat-${arming}-confirm-${boatId}`}
          onClick={() => void run(arming)}
          disabled={busy}
        >
          {busy ? 'Working…' : arming === 'decommission' ? 'Decommission' : 'Delete everything'}
        </button>
        <button
          type="button"
          className="admin-button-secondary"
          onClick={() => setArmed(null)}
          disabled={busy}
        >
          Cancel
        </button>
        {error !== null ? <span className="admin-inline-error">{error}</span> : null}
      </span>
    );
  }

  return (
    <span className="admin-confirm">
      {decommissioned ? (
        <button
          type="button"
          className="admin-button-affirm"
          data-testid={`admin-boat-restore-${boatId}`}
          onClick={() => void run('restore')}
          disabled={busy}
          title="Gives the boat back to its owners"
        >
          Restore
        </button>
      ) : (
        <button
          type="button"
          className="admin-button-danger"
          data-testid={`admin-boat-decommission-${boatId}`}
          onClick={() => setArmed('decommission')}
          disabled={busy}
        >
          Decommission
        </button>
      )}
      <button
        type="button"
        className="admin-button-danger"
        data-testid={`admin-boat-delete-${boatId}`}
        onClick={() => setArmed('delete')}
        disabled={busy}
      >
        Delete
      </button>
      {error !== null ? <span className="admin-inline-error">{error}</span> : null}
    </span>
  );
}
